import React, { useEffect, useMemo, useState } from "react";
import hrApi from "./hrApi";
import { hrOpen } from "./HrCompliance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookMarked, Send, Eye, Printer, Upload, FileText, Trash2 } from "lucide-react";

const ST = { signed: ["Signed", "bg-teal-100 text-teal-700"], opened: ["Opened", "bg-indigo-100 text-indigo-700"], sent: ["Sent", "bg-orange-100 text-orange-700"], failed: ["Not delivered", "bg-rose-100 text-rose-700"], not_sent: ["Not sent", "bg-slate-100 text-slate-500"] };

const pdfBlob = async (pid) => URL.createObjectURL((await hrApi.get(`/policies/${pid}/pdf`, { responseType: "blob" })).data);
const printPdf = async (pid) => {
  try {
    const url = await pdfBlob(pid);
    const f = document.createElement("iframe");
    f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0"; f.src = url;
    f.onload = () => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch { window.open(url, "_blank"); } };
    document.body.appendChild(f);
  } catch { toast.error("Could not open the PDF"); }
};

function UploadPolicy({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim() || !file) return toast.error("Give it a name and choose the file");
    const fd = new FormData(); fd.append("name", name); fd.append("file", file); setBusy(true);
    try { await hrApi.post("/policies", fd); toast.success("Policy added"); setOpen(false); setName(""); setFile(null); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (<>
    <Button data-testid="policy-upload-btn" variant="outline" onClick={() => setOpen(true)} className="h-9 rounded-full gap-1 text-xs"><Upload size={13} /> Add policy</Button>
    <Dialog open={open} onOpenChange={setOpen}><DialogContent>
      <DialogHeader><DialogTitle>Add a policy</DialogTitle></DialogHeader>
      <Input data-testid="policy-upload-name" placeholder="Policy name" value={name} onChange={(e) => setName(e.target.value)} />
      <input data-testid="policy-upload-file" type="file" accept="application/pdf,.docx,.doc" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
      <p className="text-[11px] text-slate-400">PDF or Word. Word files are converted to PDF; policies are always sent as PDF.</p>
      <DialogFooter><Button data-testid="policy-upload-save" disabled={busy} onClick={save}>{busy ? "Uploading…" : "Add"}</Button></DialogFooter>
    </DialogContent></Dialog>
  </>);
}

function SendDialog({ p, ids, onClose, onSent }) {
  const [ch, setCh] = useState({ email: true, sms: false });
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const list = emails.split(/[\s,;]+/).filter(Boolean);
    setBusy(true);
    try {
      const r = await hrApi.post(`/policies/${p.id}/send`, { employee_ids: ids, channels: Object.keys(ch).filter((k) => ch[k]), emails: list, note });
      toast.success(`Sent to ${r.data.sent_count} recipient(s)`);
      if (r.data.failed.length) toast.error(`${r.data.failed.length} not sent: ${r.data.failed.slice(0, 3).map((x) => `${x.employee_name || x.email} (${x.error || [x.sent?.email_error, x.sent?.sms_error].filter(Boolean).join("; ") || "failed"})`).join(", ")}`, { duration: 9000 });
      onSent(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent>
      <DialogHeader><DialogTitle>Send “{p.name}” (PDF)</DialogTitle></DialogHeader>
      <div className="text-sm text-slate-600" data-testid="policy-send-count">{ids.length} employee(s) selected. They get a link to read the PDF and sign “I have read and understood”.</div>
      {ids.length > 0 && <div className="flex gap-4 text-sm">{["email", "sms"].map((k) => <label key={k} className="inline-flex items-center gap-1"><input type="checkbox" data-testid={`policy-ch-${k}`} checked={ch[k]} onChange={(e) => setCh({ ...ch, [k]: e.target.checked })} className="accent-indigo-500" />{k.toUpperCase()}</label>)}</div>}
      <div><label className="text-xs font-semibold text-slate-600">Other email addresses (e.g. suppliers): they receive a PDF download link</label>
        <Textarea data-testid="policy-emails" value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="name@supplier.com.au, other@company.com" className="mt-1 min-h-[60px] text-sm" /></div>
      <div><label className="text-xs font-semibold text-slate-600">Message (optional)</label><Input data-testid="policy-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" /></div>
      <DialogFooter><Button data-testid="policy-send-confirm" disabled={busy || (ids.length > 0 && !ch.email && !ch.sms)} onClick={send} className="gap-1"><Send size={14} /> {busy ? "Sending…" : "Send PDF"}</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function Roster({ p, data, reload }) {
  const [filter, setFilter] = useState("outstanding");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [sending, setSending] = useState(false);
  const rows = data.employees;
  const list = useMemo(() => rows.filter((r) => (filter === "all" || (filter === "signed" ? r.status === "signed" : r.status !== "signed")) && (!q || (r.employee_name || "").toLowerCase().includes(q.toLowerCase()))), [rows, filter, q]);
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const allSel = list.length > 0 && list.every((r) => sel[r.employee_id]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        {[["outstanding", "Not signed"], ["signed", "Signed"], ["all", "All"]].map(([k, l]) => (
          <button key={k} data-testid={`policy-filter-${k}`} onClick={() => setFilter(k)} className={`px-3 h-8 rounded-full text-xs font-semibold ${filter === k ? "bg-indigo-500 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>))}
        <Input data-testid="policy-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name" className="h-8 w-48 text-sm" />
        <Button data-testid="policy-send" onClick={() => setSending(true)} className="ml-auto h-9 rounded-full gap-1 text-xs bg-indigo-600"><Send size={13} /> Send PDF{ids.length ? ` to ${ids.length}` : ""}</Button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden"><div className="max-h-[480px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 sticky top-0"><tr>
            <th className="p-2 w-8"><input type="checkbox" data-testid="policy-sel-all" checked={allSel} onChange={(e) => setSel((s) => ({ ...s, ...Object.fromEntries(list.map((r) => [r.employee_id, e.target.checked])) }))} /></th>
            <th className="p-2 text-left">Employee</th><th className="p-2 text-left">Status</th><th className="p-2"></th></tr></thead>
          <tbody>{list.map((r) => (
            <tr key={r.employee_id} className="border-t border-slate-50" data-testid={`policy-row-${r.employee_id}`}>
              <td className="p-2"><input type="checkbox" checked={!!sel[r.employee_id]} onChange={(e) => setSel({ ...sel, [r.employee_id]: e.target.checked })} /></td>
              <td className="p-2"><div className="font-medium text-slate-800">{r.employee_name}</div><div className="text-[11px] text-slate-400">{r.department}{!r.has_mobile && " · no mobile"}{!r.has_email && " · no email"}</div></td>
              <td className="p-2"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${ST[r.status][1]}`}>{ST[r.status][0]}</span>
                <span className="text-[11px] text-slate-400 ml-2">{r.signed_at ? new Date(r.signed_at).toLocaleDateString("en-AU") : r.sent_at ? `sent ${new Date(r.sent_at).toLocaleDateString("en-AU")}` : ""}</span></td>
              <td className="p-2 text-right">{r.document_id && <button data-testid={`policy-doc-${r.employee_id}`} onClick={() => hrOpen(`/documents/${r.document_id}/file`)} className="text-indigo-600 inline-flex items-center gap-1 text-xs"><FileText size={13} /> Signed PDF</button>}</td>
            </tr>))}
            {!list.length && <tr><td colSpan={4} className="p-6 text-center text-slate-400">No employees here</td></tr>}</tbody>
        </table>
      </div></div>
      {data.external.length > 0 && <div className="text-xs text-slate-500" data-testid="policy-external">Also emailed to: {data.external.map((x) => `${x.email}${x.opened_at ? " (opened)" : ""}`).join(", ")}</div>}
      {sending && <SendDialog p={p} ids={ids} onClose={() => setSending(false)} onSent={() => { setSel({}); reload(); }} />}
    </div>
  );
}

export default function PoliciesAdmin() {
  const [pols, setPols] = useState([]);
  const [pid, setPid] = useState(null);
  const [data, setData] = useState(null);
  const loadPols = () => hrApi.get("/policies").then((r) => { setPols(r.data); setPid((c) => (r.data.some((x) => x.id === c) ? c : r.data[0]?.id || null)); });
  const loadRoster = () => pid && hrApi.get(`/policies/${pid}/roster`).then((r) => setData(r.data));
  useEffect(() => { loadPols(); }, []);
  useEffect(() => { setData(null); loadRoster(); }, [pid]);
  const p = pols.find((x) => x.id === pid);
  const view = async () => { try { window.open(await pdfBlob(pid), "_blank"); } catch { toast.error("Could not open the PDF"); } };
  const archive = async () => {
    if (!window.confirm(`Remove "${p.name}" from the list? Signed records are kept.`)) return;
    await hrApi.delete(`/policies/${pid}`); toast.success("Policy removed"); loadPols();
  };
  return (
    <div className="space-y-4" data-testid="policies-admin">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-heading font-extrabold text-2xl text-slate-800 inline-flex items-center gap-2"><BookMarked size={22} /> Policies</h1>
        <span className="ml-auto"><UploadPolicy onDone={loadPols} /></span>
      </div>
      <p className="text-slate-500 text-sm -mt-2">View, print or send company policies. Everything is sent as a PDF; employees sign to confirm they have read and understood it.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">{pols.map((x) => (
        <button key={x.id} data-testid={`policy-card-${x.id}`} onClick={() => setPid(x.id)} className={`rounded-2xl border px-4 py-3 text-left transition-colors ${x.id === pid ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-white hover:border-indigo-200"}`}>
          <div className="font-semibold text-slate-800 text-sm">{x.name}</div>
          <div className="text-xs text-slate-500">PDF · {x.pages} page{x.pages === 1 ? "" : "s"} · {x.acknowledged}/{x.active_employees} signed</div>
        </button>))}</div>
      {p && <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-heading font-bold text-slate-800" data-testid="policy-selected-name">{p.name}</h2>
          <div className="ml-auto flex gap-2">
            <Button data-testid="policy-view" variant="outline" onClick={view} className="h-9 rounded-full gap-1 text-xs"><Eye size={13} /> View PDF</Button>
            <Button data-testid="policy-print" variant="outline" onClick={() => printPdf(pid)} className="h-9 rounded-full gap-1 text-xs"><Printer size={13} /> Print</Button>
            <Button data-testid="policy-archive" variant="outline" onClick={archive} className="h-9 rounded-full gap-1 text-xs text-rose-600"><Trash2 size={13} /> Remove</Button>
          </div>
        </div>
        {data ? <Roster p={p} data={data} reload={() => { loadRoster(); loadPols(); }} /> : <div className="text-sm text-slate-400">Loading…</div>}
      </div>}
    </div>
  );
}
